import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  TextInput,
  Textarea,
  Alert,
  Spinner,
  Badge,
  Pagination,
} from 'flowbite-react';
import { fetchWithAuth } from '../utils/api';
import { getMarketersEndpoints } from '../config/api';
import { getValidationMessages, formatDate } from '../utils/helper';

interface Marketer {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface MarketerFormData {
  name: string;
}

const Marketers: React.FC = () => {
  const [marketers, setMarketers] = useState<Marketer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);
  const [messagePopup, setMessagePopup] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingMarketer, setEditingMarketer] = useState<Marketer | null>(null);

  // Form states
  const [formData, setFormData] = useState<MarketerFormData>({
    name: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load marketers
  const loadMarketers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const marketersEndpoint = getMarketersEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
        ...(search && { search: search }),
      });

      const response = await fetchWithAuth(`${marketersEndpoint}?${params}`);

      if (response.success && response.data) {
        setMarketers(response.data.results || []);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
      } else {
        setError('Không thể tải danh sách marketer');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load marketers error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMarketers(1, searchTerm);
  }, []);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    loadMarketers(1, searchTerm);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadMarketers(page, searchTerm);
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setEditingMarketer(null);
    setFormData({
      name: '',
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Open edit modal
  const openEditModal = (marketer: Marketer) => {
    setModalMode('edit');
    setEditingMarketer(marketer);
    setFormData({
      name: marketer.name,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Handle form input change
  const handleInputChange = (field: keyof MarketerFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Tên marketer là bắt buộc';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const marketersEndpoint = getMarketersEndpoints();
      const endpoint =
        modalMode === 'create'
          ? marketersEndpoint.CREATE
          : `${marketersEndpoint.UPDATE}/${editingMarketer?.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify({
          ...formData,
        }),
      });

      if (response.success) {
        setMessagePopup({
          type: 'success',
          text: `Marketer ${
            modalMode === 'create' ? 'đã được tạo' : 'đã được cập nhật'
          } thành công!`,
        });
        setShowModal(false);
        loadMarketers(currentPage);
      } else {
        setMessagePopup({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            `Không thể ${modalMode === 'create' ? 'tạo' : 'cập nhật'} marketer`,
        });
      }
    } catch (error) {
      setMessagePopup({
        type: 'failure',
        text: `Đã xảy ra lỗi khi ${
          modalMode === 'create' ? 'tạo' : 'cập nhật'
        } marketer`,
      });
      console.error('Submit marketer error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (marketerId: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa marketer này?')) {
      return;
    }

    try {
      const marketersEndpoint = getMarketersEndpoints();
      const response = await fetchWithAuth(
        `${marketersEndpoint.DELETE}/${marketerId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Marketer đã được xóa thành công!',
        });
        loadMarketers(currentPage, searchTerm);
      } else {
        setMessage({
          type: 'failure',
          text: 'Không thể xóa marketer',
        });
      }
    } catch (error) {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi xóa marketer',
      });
      console.error('Delete marketer error:', error);
    }
  };

  if (loading && marketers.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải danh sách marketer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Marketers</h1>
            <p className="text-gray-600">Quản lý danh sách marketer</p>
          </div>
          <Button onClick={openCreateModal} className="btn-primary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Tạo Marketer
          </Button>
        </div>
      </div>

      {message && (
        <Alert color={message.type} className="mb-4">
          <span>{message.text}</span>
        </Alert>
      )}

      {/* Search and Filter */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput
              placeholder="Tìm kiếm theo tên"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Button onClick={handleSearch} className="btn-primary">
              Tìm kiếm
            </Button>
            <Button
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
                loadMarketers(1, '');
              }}
              className="btn-reset"
            >
              Xóa bộ lọc
            </Button>
          </div>
        </div>
      </Card>

      {/* Marketers Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>Tên</Table.HeadCell>
              <Table.HeadCell>Ngày tạo</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {marketers.map(marketer => (
                <Table.Row
                  key={marketer.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {marketer.name}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(marketer.created_at)}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => openEditModal(marketer)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="sm"
                        color="red"
                        onClick={() => handleDelete(marketer.id)}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4">
            <div className="text-sm text-gray-700">
              Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
              {totalItems} kết quả
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="2xl">
        <Modal.Header>
          {modalMode === 'create' ? 'Tạo Marketer Mới' : 'Chỉnh Sửa Marketer'}
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {messagePopup && (
              <Alert color={messagePopup.type} className="mb-4">
                <span>{messagePopup.text}</span>
              </Alert>
            )}
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên Marketer *
                </label>
                <TextInput
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  placeholder="Nhập tên marketer"
                  color={formErrors.name ? 'failure' : undefined}
                  helperText={formErrors.name}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="gray"
              onClick={() => setShowModal(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Đang xử lý...
                </>
              ) : modalMode === 'create' ? (
                'Tạo Marketer'
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default Marketers;
